import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OsAgenciaService } from '../../../../services/os-agencia.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { OsAgenciaDetailDto } from '@opb-workspace/shared';

@Component({
  selector: 'app-service-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    FormsModule
  ],
  templateUrl: './service-order-detail.component.html',
  styleUrls: ['./service-order-detail.component.scss'],
})
export class ServiceOrderDetailComponent implements OnInit {
  osNumber: number = 0;
  orderDetail: OsAgenciaDetailDto | null = null;
  loading = true;
  error: string | null = null;

  constructor(
    private osAgenciaService: OsAgenciaService,
    private route: ActivatedRoute,
    private router: Router,
    private logger: LoggerService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.osNumber = Number(id);
      this.fetchOrderDetail(this.osNumber);
    } else {
      this.error = 'No se proporcionó número de orden de servicio';
      this.loading = false;
    }
  }

  goBack(): void {
    // Preservar query params al regresar
    const queryParams = this.route.snapshot.queryParams;
    this.router.navigate(['/ordenes-servicio-agencia'], {
      queryParams: queryParams,
    });
  }

  fetchOrderDetail(osNumber: number): void {
    this.loading = true;
    this.error = null;

    this.osAgenciaService
      .getOrderDetail(osNumber)
      .pipe(
        catchError((error) => {
          this.logger.error(
            'Error al cargar detalle de orden de servicio',
            error as Error,
            'ServiceOrderDetailComponent'
          );
          this.error = 'Error al cargar los datos de la orden de servicio';
          this.snackBar.open(
            'Error al cargar detalle de orden de servicio',
            'Cerrar',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            }
          );
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((response: any) => {
        // El backend retorna OsAgenciaDetailDto directamente
        const data = response?.success && response.data ? response.data : response;
        if (data) {
          this.orderDetail = data;
          this.logger.info(
            `✅ Detalle de orden de servicio cargado: ${osNumber}`,
            'ServiceOrderDetailComponent'
          );
        } else {
          this.error = 'No se encontraron datos para la orden de servicio';
        }
      });
  }

  padNumber(num: number): string {
    return String(num).padStart(8, '0');
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    
    const formatted = new Intl.NumberFormat('es-CL', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    
    return `$ ${formatted}`;
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    return new Intl.DateTimeFormat('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dateObj);
  }

  /**
   * Verifica si la orden de servicio es de tipo "Extra Costos"
   * Una O/S es de extra costos cuando NO tiene ShippingInstructionId ni TransportOrderId
   */
  isExtraCostos(): boolean {
    return !this.orderDetail?.nroIE && !this.orderDetail?.transportOrderId;
  }
}

